import { AlertTriangleIcon, Loader2, LockIcon } from "lucide-react"
import { useEffect, useId, useState } from "react"
import { Alert, AlertDescription } from "@/ui/alert"
import { Button } from "@/ui/button"
import { Input } from "@/ui/input"
import { Label } from "@/ui/label"
import { Heading, Muted } from "@/ui/typography"

interface SignInProps {
  email?: string
  error?: string
  mode?: "login" | "register"
  isPending?: boolean
  /** When false, hide register toggle and force login-only UI. */
  allowRegistration?: boolean
}

export const SignIn = ({
  email,
  error,
  mode: initialMode = "login",
  isPending,
  allowRegistration = false,
}: SignInProps) => {
  const [mode, setMode] = useState<"login" | "register">(
    allowRegistration ? initialMode : "login",
  )
  const emailInputId = useId()
  const passwordInputId = useId()

  useEffect(() => {
    setMode(allowRegistration ? initialMode : "login")
  }, [initialMode, allowRegistration])

  const isRegister = allowRegistration && mode === "register"

  return (
    <div className="w-full max-w-xs space-y-10">
      <div className="text-center">
        <LockIcon className="mx-auto size-8" />
        <Heading level={2} className="text-center">
          {isRegister ? "Create account" : "Sign in"}
        </Heading>
        <Muted>
          {isRegister
            ? "Register with email and password"
            : "Enter your email and password"}
        </Muted>
      </div>

      <div className="space-y-4">
        <input
          type="hidden"
          name="mode"
          value={isRegister ? "register" : "login"}
        />

        {error && (
          <Alert variant="destructive">
            <AlertTriangleIcon />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-3">
          <Label htmlFor={emailInputId}>Email</Label>
          <Input
            type="email"
            id={emailInputId}
            name="email"
            defaultValue={email}
            autoComplete="email"
            placeholder="Enter your email"
            required
          />
        </div>

        <div className="space-y-3">
          <Label htmlFor={passwordInputId}>Password</Label>
          <Input
            type="password"
            id={passwordInputId}
            name="password"
            autoComplete={isRegister ? "new-password" : "current-password"}
            placeholder={
              isRegister
                ? "Create a password (min 8 chars)"
                : "Enter your password"
            }
            required
            minLength={8}
          />
        </div>
      </div>

      <div className="space-y-3">
        <Button type="submit" className="w-full" disabled={isPending}>
          {isPending ? <Loader2 className="size-4 animate-spin" /> : null}
          {isRegister ? "Create account" : "Sign in"}
        </Button>

        {allowRegistration ? (
          <Button
            type="button"
            variant="ghost"
            className="w-full"
            disabled={isPending}
            onClick={() => setMode(isRegister ? "login" : "register")}
          >
            {isRegister
              ? "Already have an account? Sign in"
              : "Need an account? Create one"}
          </Button>
        ) : null}
      </div>
    </div>
  )
}
